import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchProjectFases, createProjectFase } from '@/api/planning';
import type { FaseResult } from '@/api/planning';
import { usePlanningStore } from '@/stores/planning';

interface FaseSelectorProps {
  projectId: number;
  projectName: string;
  address?: string;
  onClose: () => void;
}

export function FaseSelector({ projectId, projectName, address, onClose }: FaseSelectorProps) {
  const [fases, setFases] = useState<FaseResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newFaseName, setNewFaseName] = useState('');
  const [creating, setCreating] = useState(false);

  const enterScheduleProjectMode = usePlanningStore((s) => s.enterScheduleProjectMode);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchProjectFases(projectId)
      .then((result) => {
        setFases(result.fases);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Fases laden mislukt');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleSelectFase(fase: FaseResult) {
    enterScheduleProjectMode(projectId, projectName, fase.id, fase.name, address);
    onClose();
  }

  async function handleCreateFase() {
    if (!newFaseName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await createProjectFase(projectId, newFaseName.trim());
      enterScheduleProjectMode(projectId, projectName, result.id, result.name, address);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fase aanmaken mislukt');
      setCreating(false);
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Selecteer fase"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Content panel */}
      <div className="relative z-10 bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="text-base font-bold text-td-navy truncate">
            Selecteer fase — {projectName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-td-muted hover:text-td-navy"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {address && (
          <p className="px-5 text-xs text-td-muted truncate">{address}</p>
        )}

        {/* Loading */}
        {loading && (
          <div className="px-5 py-8 text-center">
            <div
              className="inline-block w-6 h-6 border-3 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
              role="status"
            >
              <span className="sr-only">Laden...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 bg-red-50 text-red-600 p-3 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Fases list */}
        {!loading && fases.length > 0 && (
          <div className="px-5 pt-3 pb-2">
            <ul className="divide-y divide-gray-100">
              {fases.map((fase) => (
                <li key={fase.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectFase(fase)}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between gap-2"
                  >
                    <span className="text-sm font-medium text-td-navy truncate">{fase.name}</span>
                    {fase.childCount > 0 && (
                      <span className="text-[10px] font-semibold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded-full shrink-0">
                        {fase.childCount}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && fases.length === 0 && !error && (
          <p className="px-5 py-4 text-sm text-td-muted">Geen fases gevonden</p>
        )}

        {/* Divider */}
        {!loading && (
          <div className="mx-5 border-t border-gray-200 mt-2" />
        )}

        {/* New fase input */}
        {!loading && (
          <div className="px-5 py-4">
            <label htmlFor="new-fase-input" className="text-xs font-semibold text-td-muted block mb-1.5">
              Nieuwe fase
            </label>
            <div className="flex items-center gap-2">
              <input
                id="new-fase-input"
                type="text"
                value={newFaseName}
                onChange={(e) => setNewFaseName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFase(); }}
                placeholder="Fasenaam..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
              />
              <button
                type="button"
                onClick={handleCreateFase}
                disabled={!newFaseName.trim() || creating}
                className="px-4 py-2 text-sm font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-50"
              >
                {creating ? 'Bezig...' : 'Aanmaken'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
